import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// project imports
import { openSnackbar } from 'api/snackbar';
import useAuth from 'hooks/useAuth';
import { useTranslation } from 'hooks/useTranslation';
import { getRoleRedirectPath, hasAnyRole } from 'utils/role-utils';

// types
import { RoleGuardProps } from 'types/auth';
import { SnackbarProps } from 'types/snackbar';

const getLoginPath = (allowedRoles?: string[]): string => {
  if (allowedRoles && (allowedRoles.includes('admin') || allowedRoles.includes('manager'))) {
    return '/auth/login/email';
  }
  return '/auth/login';
};

// ==============================|| ROLE GUARD ||============================== //

export default function RoleGuard({ children, allowedRoles, fallbackPath, fallbackMessage }: RoleGuardProps) {
  const { isLoggedIn, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useTranslation();

  useEffect(() => {
    if (!isLoggedIn || !user?.role) {
      const loginPath = getLoginPath(allowedRoles);

      navigate(loginPath, {
        state: {
          from: location.pathname,
          message: t('Please log in to access this page')
        },
        replace: true
      });
      return;
    }

    if (user?.role && !hasAnyRole(user.role, allowedRoles as any[])) {
      const defaultMessage = t('You do not have permission to access this page.');

      const message = fallbackMessage || defaultMessage;

      openSnackbar({
        open: true,
        message,
        variant: 'alert',
        alert: {
          color: 'error'
        }
      } as SnackbarProps);

      const redirectPath = fallbackPath || getRoleRedirectPath(user.role);

      navigate(redirectPath, {
        state: {
          from: location.pathname,
          message
        },
        replace: true
      });
    }
  }, [isLoggedIn, user, allowedRoles, navigate, location, fallbackPath, fallbackMessage, t]);

  if (!isLoggedIn || (user?.role && !hasAnyRole(user.role, allowedRoles as any[]))) {
    return null;
  }

  return children;
}
